import CloseIcon from '@mui/icons-material/Close';
import { Checkbox } from '@mui/material';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { serialize } from 'object-to-formdata';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Modal from 'react-modal';
import { IPixAccountViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { ReactComponent as EditPen } from '../../../assets/icons/editPen.svg';
import { ReactComponent as ExpandArrowIcon } from '../../../assets/icons/iconExpandArrow.svg';
import { MASK_CNPJ, MASK_CPF, MASK_PHONE } from '../../../constants';
import { useAuth } from '../../../context';
import { usePixAccounts } from '../../../context/pixAccounts';
import { WalletController } from '../../../controller/WalletController';
import { labelPixType, PixType } from '../../../types/enums';
import { validateEmail } from '../../../utils/validateEmail';
import { BasicButton } from '../../Buttons/BasicButton';
import { Input } from '../../Inputs/Input';
import { SelectControled } from '../../SelectControled';
import { Form, Header } from './style';
import { useAlert } from '../../../hooks/useAlert';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { unMask } from '../../../utils/removeMask';

interface ModalEditPixAccountProps {
  isOpen: boolean;
  runningTutorial: boolean;
  currentPixAccount: IPixAccountViewModel;
  onRequestClose: () => void;
}

export function ModalEditPixAccount({
  isOpen,
  currentPixAccount,
  runningTutorial,
  onRequestClose,
}: ModalEditPixAccountProps) {
  const theme = useTheme();
  const { user } = useAuth();
  const alert = useAlert();
  const { setPixAccounts } = usePixAccounts();
  const [isPreferred, setIsPreferred] = useState<boolean>(false);
  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: theme.colors.background_white,
        color: theme.colors.secondary,
        maxHeight: 250,
      },
    },
  };
  const schema = yup.object().shape({
    key: yup.string().required('Campo obrigatório'),
    holderName: yup.string().required('Campo obrigatório'),
    holderDocument: yup
      .string()
      .required('Campo obrigatório')
      .test('cpfCnpjValid', 'CPF ou CNPJ inválido', (value: string) => {
        if (!value) return false;
        const cleanValue = value.replace(/\D/g, '');

        if (cleanValue.length === 11) {
          return cpf.isValid(cleanValue);
        }

        if (cleanValue.length === 14) {
          return cnpj.isValid(cleanValue);
        }

        return false;
      }),
  });

  const [defaultValuesPix] = useState({
    type: Number(currentPixAccount.type),
    key: currentPixAccount.key,
    holderName: currentPixAccount.holderName || '',
    holderDocument: currentPixAccount.holderDocument || '',
  });

  const {
    formState: { errors: errorsPix },
    control,
    setError,
    setValue,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: defaultValuesPix,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const [key, type, holderName, holderDocument] = useWatch({
    control,
    name: ['key', 'type', 'holderName', 'holderDocument'],
  });

  const handleUpdatePixAccount = () => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! O PIX não será atualizado.',
        type: 'success',
      });
      onRequestClose();
      return;
    }
    let asError = false;

    if (Number(type) === PixType.PHONE) {
      if (key.length !== 15) {
        setError('key', { type: 'manual', message: 'Número de telefone inválido' });
        asError = true;
      }
    }

    if (Number(type) === PixType.MAIL) {
      if (!validateEmail(key)) {
        setError('key', { type: 'manual', message: 'Email inválido' });
        asError = true;
      }
    }

    if (Number(type) === PixType.CPF) {
      if (!cpf.isValid(key)) {
        setError('key', { type: 'manual', message: 'CPF inválido' });
        asError = true;
      }
    }

    if (Number(type) === PixType.CNPJ) {
      if (!cnpj.isValid(key)) {
        setError('key', { type: 'manual', message: 'CNPJ inválido' });
        asError = true;
      }
    }

    if (!asError) {
      const pixAccount: IPixAccountViewModel = {
        id: currentPixAccount.id,
        type: Number(type),
        key,
        holderDocument: unMask(holderDocument),
        holderName,
        isPreferred,
      };

      const formData = serialize(pixAccount, { indices: true });
      WalletController.updatePixAccount(formData, user?.id as string)
        .then(() => {
          reset();
          onRequestClose();
          alert.show({
            message: 'Pix atualizado com sucesso.',
            type: 'success',
          });
          setPixAccounts((prevState) => {
            const idxPixAccount = prevState.findIndex((p) => p.id === pixAccount.id);
            if (idxPixAccount !== -1) {
              const newPixAccounts = [...prevState];
              newPixAccounts[idxPixAccount] = pixAccount;
              return newPixAccounts;
            }
            return prevState;
          });
        })
        .catch((error) => {
          if (error?.response?.data === 'ALREADY_REGISTERED') {
            alert.show({
              message:
                'Você está tentando adicionar uma chave PIX já existente. Tente novamente com outra chave.',
              type: 'error',
            });
          } else {
            alert.show({
              message: 'Ocorreu um erro ao atualizar a chave PIX.',
              type: 'error',
            });
          }
        });
    }
  };

  useEffect(() => {
    setIsPreferred(currentPixAccount.isPreferred);
    setValue('type', currentPixAccount.type);
    setValue('key', currentPixAccount.key);
    setValue('holderName', currentPixAccount?.holderName || '');
    setValue('holderDocument', currentPixAccount?.holderDocument || '');
  }, [currentPixAccount, setValue]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-edit-modal-content">
      <Header>
        <div className="subHeader">
          <EditPen />
          <h1>Editar Chave Pix</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <Form>
        <div className="cnt-form">
          <div className="form-pix">
            <SelectControled
              name="type"
              control={control}
              MenuProps={MenuProps}
              IconComponent={ExpandArrowIcon}
              defaultValue={currentPixAccount.type}
              onChange={(e) => {
                setValue('key', '');
                setValue('type', e.target.value as number);
              }}
              placeholderTitle="Selecione"
              itens={Object.keys(PixType)
                .filter((pixType) => !isNaN(Number(pixType)))
                .map((pixKey) => ({
                  id: pixKey,
                  value: labelPixType[pixKey as unknown as PixType],
                }))}
            />
            <Input
              name="key"
              placeholder="Chave PIX"
              className="input-form"
              mask={
                Number(type) === PixType.CPF
                  ? MASK_CPF
                  : Number(type) === PixType.CNPJ
                  ? MASK_CNPJ
                  : Number(type) === PixType.PHONE
                  ? MASK_PHONE
                  : ''
              }
              control={control}
              variant="standard"
              errorText={errorsPix.key?.message}
            />
            <Input
              name="holderName"
              placeholder="Nome do titular"
              className="input-form"
              control={control}
              variant="standard"
              errorText={errorsPix.holderName?.message}
            />
            <Input
              name="holderDocument"
              placeholder="CPF/CNPJ do titular"
              className="input-form"
              mask={holderDocument?.length <= 14 ? MASK_CPF : MASK_CNPJ}
              control={control}
              variant="standard"
              errorText={errorsPix.holderDocument?.message}
            />
          </div>
          <div className="checkbox-options">
            <Checkbox
              checked={isPreferred}
              onChange={() => {
                setIsPreferred(!isPreferred);
              }}
            />
            Chave preferencial
          </div>
        </div>
        <div className="buttonModal">
          <BasicButton
            variant="outlined"
            size="large"
            onClick={handleSubmit(handleUpdatePixAccount)}>
            Salvar alterações
          </BasicButton>
        </div>
      </Form>
    </Modal>
  );
}
